import { useMemo } from 'react'
import {
  useNavigate,
  useLocation,
  useParams as useRRParams,
  useSearchParams as useRRSearchParams,
} from 'react-router-dom'

import {
  planEmbedNavigation,
  postStudyEmbedMessage,
  STUDY_EMBED_SOURCE,
} from '@/features/studies/embed/studyEmbedBridge'

export function useRouter() {
  const navigate = useNavigate()
  const location = useLocation()
  return useMemo(
    () => ({
      push: (href: string, opts?: { scroll?: boolean; state?: unknown }) => {
        const target = embedTarget(href, location)
        if (target === null) {
          return
        }
        navigate(target, opts?.state !== undefined ? { state: opts.state } : undefined)
      },
      replace: (href: string) => {
        const target = embedTarget(href, location)
        if (target === null) {
          return
        }
        navigate(target, { replace: true })
      },
      prefetch: async () => {},
      back: () => navigate(-1),
      forward: () => navigate(1),
      refresh: () => {
        window.location.reload()
      },
    }),
    [navigate, location],
  )
}

/**
 * In the study pane, a move that stays on the creator or the results page
 * keeps `embed=thread`. Anything else is told to the parent and does not
 * change this frame. Outside that pane the href is unchanged.
 */
function embedTarget(
  href: string,
  location: { pathname: string; search: string },
): string | null {
  const plan = planEmbedNavigation(href, {
    pathname: location.pathname,
    search: location.search,
  })
  if (plan.action === 'open-top') {
    postStudyEmbedMessage({
      source: STUDY_EMBED_SOURCE,
      type: 'open-top',
      href: plan.href,
    })
    return null
  }
  return plan.href
}

export function usePathname(): string {
  return useLocation().pathname
}

export function useParams<
  T extends Record<string, string | undefined> = Record<
    string,
    string | undefined
  >,
>() {
  return useRRParams() as T
}

export function useSearchParams(): URLSearchParams {
  const [sp] = useRRSearchParams()
  return sp
}

/** Client-side hard redirect (replaces SSR `redirect()` from the old stack). */
export function redirect(url: string): never {
  window.location.replace(url)
  throw new Error('redirect')
}
